import { supabase } from "./supabase";
import { retryRpc } from "./supabase-retry";
import { classifyCheckoutError } from "./checkout-errors";
import { emit, EVENTS } from "./event-bus";

/**
 * Checkout workflow engine with automatic rollback.
 * Each step has an execute() and an optional compensate() (undo).
 * If a critical step fails, all previous steps are compensated in reverse order.
 * Non-critical side effects are decoupled via the event bus (Medusa pattern).
 */

export async function runCheckoutWorkflow({ orderId, user, cart, totals, appliedCoupon, paymentMethod, resolvedAddress, resolvedBillingAddress, paymentHandles, storeCreditToApply }) {
  const { discount, shipping, total, totalBeforeCredit } = totals;
  const refCode = localStorage.getItem("_ref");
  const campaignId = sessionStorage.getItem("_cid");

  const completedSteps = [];
  const errors = [];
  let placeOrderData = null;

  // --- Define workflow steps ---

  const criticalSteps = [
    {
      name: "place_order",
      execute: async () => {
        const { data, error } = await retryRpc(supabase, "place_order", {
          p_order_id: orderId,
          p_user_id: user.id,
          p_items: cart.map(item => ({ product_id: item.id, name: item.name, qty: item.qty, price: item.price })),
          p_total: total,
          p_shipping: shipping,
          p_payment_method: paymentMethod,
          p_shipping_address: resolvedAddress,
          p_billing_address: paymentMethod === "card" ? (resolvedBillingAddress ?? resolvedAddress) : null,
          p_coupon_code: appliedCoupon?.code ?? null,
          p_coupon_discount: discount,
          p_store_credit_applied: storeCreditToApply,
          p_affiliate_code: refCode ?? null,
          p_campaign_id: campaignId ?? null,
        });
        if (error) throw new Error(error.message);
        if (data && !data.ok) throw new Error(data.error || "Order failed");
        placeOrderData = data;
        return data;
      },
      compensate: async () => {
        await supabase.rpc("cancel_order", { p_order_id: orderId }).catch(err => console.error("Rollback failed (cancel_order):", err));
      },
    },
    ...(storeCreditToApply > 0
      ? [
          {
            name: "apply_store_credit",
            execute: async () => {
              const { data, error } = await retryRpc(supabase, "apply_store_credit", {
                p_user_id: user.id,
                p_order_id: orderId,
                p_amount: storeCreditToApply,
              });
              if (error) throw new Error(error.message);
              if (data && !data.ok) throw new Error(data.error || "Store credit failed");
            },
            compensate: async () => {
              await supabase.rpc("refund_store_credit", {
                p_user_id: user.id,
                p_order_id: orderId,
                p_amount: storeCreditToApply,
              }).catch(err => console.error("Rollback failed (refund_store_credit):", err));
            },
          },
        ]
      : []),
  ];

  // --- Execute critical steps sequentially ---

  for (const step of criticalSteps) {
    try {
      await step.execute();
      completedSteps.push(step);
    } catch (err) {
      const errorType = classifyCheckoutError(err.message);
      errors.push({ step: step.name, error: err.message, errorType, critical: true });

      for (let i = completedSteps.length - 1; i >= 0; i--) {
        if (completedSteps[i].compensate) {
          try {
            await completedSteps[i].compensate();
          } catch (compErr) {
            errors.push({ step: completedSteps[i].name, error: compErr.message, rollback: true });
          }
        }
      }

      return { ok: false, errors, errorType, rolledBack: true };
    }
  }

  // --- Emit order.placed — subscribers handle side effects independently ---

  // 10-pack vial discount (#119): the server-confirmed figure from place_order
  // wins; the client's own pricing is the fallback for a legacy RPC response.
  const placed = Array.isArray(placeOrderData) ? placeOrderData[0] : placeOrderData;
  const bulkDiscount = Number(placed?.bulk_vial_discount ?? totals.bulkSavings ?? 0) || 0;

  const eventResults = await emit(EVENTS.ORDER_PLACED, {
    orderId, user, cart, totals, appliedCoupon,
    paymentMethod, resolvedAddress, paymentHandles,
    refCode, totalBeforeCredit, bulkDiscount,
  });

  for (const r of eventResults) {
    if (!r.ok) {
      errors.push({ step: r.handler, error: r.error, critical: false });
    }
  }

  return { ok: true, orderId, data: placeOrderData, errors: errors.length > 0 ? errors : null };
}
